import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import {
  getLiquidacionesComisiones,
} from "../helpers/rutaComisiones";

import logo from "../images/ridersmiami-logo.png";

/* =========================================================
   FORMATOS
   ========================================================= */

const formatDate = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const m = value.match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

    if (m) {
      return `${m[3]}/${m[2]}/${m[1]}`;
    }
  }

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  return d.toLocaleDateString("es-AR");
};

const formatUSD = (value) => {
  const n = Number(value || 0);

  return n.toLocaleString("es-AR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
};

const formatUSDPdf = (value) => {
  const n = Number(value || 0);

  return `US$ ${n.toLocaleString("es-AR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const cargarImagen = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();

    img.crossOrigin = "Anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const getId = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    return value;
  }

  return value._id || value.id || "";
};

const nombreVendedor = (item) => {
  const vendedor = item?.vendedor;

  if (!vendedor) {
    return "-";
  }

  if (typeof vendedor === "string") {
    return vendedor;
  }

  return (
    [
      vendedor.nombres,
      vendedor.apellidos,
    ]
      .filter(Boolean)
      .join(" ") ||
    vendedor.email ||
    "-"
  );
};

const textoEstado = (estado) => {
  const value = String(
    estado || ""
  ).toUpperCase();

  switch (value) {
    case "PENDIENTE_PAGO":
      return "Pendiente";

    case "PARCIAL":
      return "Parcial";

    case "PAGADA":
      return "Pagada";

    case "ANULADA":
      return "Anulada";

    default:
      return value || "-";
  }
};

const colorEstado = (estado) => {
  const value = String(
    estado || ""
  ).toUpperCase();

  switch (value) {
    case "PENDIENTE_PAGO":
      return "warning";

    case "PARCIAL":
      return "info";

    case "PAGADA":
      return "success";

    case "ANULADA":
      return "danger";

    default:
      return "secondary";
  }
};

const montoBono = (item) => {
  if (
    String(item?.tipo || "").toUpperCase() ===
    "MENSUAL"
  ) {
    return Number(
      item?.bonoMensualUSD || 0
    );
  }

  return Number(
    item?.bonoSemanalUSD || 0
  );
};

/* =========================================================
   COMPONENTE
   ========================================================= */

export default function ReporteComisionesLiquidaciones() {
  const [desde, setDesde] =
    useState("");

  const [hasta, setHasta] =
    useState("");

  const [sede, setSede] =
    useState("");

  const [vendedor, setVendedor] =
    useState("");

  const [tipo, setTipo] =
    useState("");

  const [estado, setEstado] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  const [
    liquidaciones,
    setLiquidaciones,
  ] = useState([]);

  const [
    vendedoresBase,
    setVendedoresBase,
  ] = useState([]);

  const [resumen, setResumen] =
    useState({
      cantidad: 0,
      totalLiquidadoUSD: 0,
      totalPagadoUSD: 0,
      saldoPendienteUSD: 0,
    });

  /* =======================================================
     VENDEDORES
     ======================================================= */

  const vendedoresDisponibles =
    useMemo(() => {
      const map = new Map();

      vendedoresBase.forEach(
        (item) => {
          const id =
            getId(item?.vendedor);

          const nombre =
            nombreVendedor(item);

          if (
            id &&
            nombre &&
            !map.has(id)
          ) {
            map.set(id, nombre);
          }
        }
      );

      return Array.from(
        map.entries()
      )
        .map(([id, nombre]) => ({
          id,
          nombre,
        }))
        .sort((a, b) =>
          a.nombre.localeCompare(
            b.nombre,
            "es"
          )
        );
    }, [vendedoresBase]);

  /* =======================================================
     CARGAR
     ======================================================= */

  const cargarReporte = async (
    cargaInicial = false
  ) => {
    setLoading(true);
    setError("");

    try {
      const data =
        await getLiquidacionesComisiones({
          desde,
          hasta,
          sede,
          vendedor,
          tipo,
          estado,
        });

      const registros =
        Array.isArray(
          data?.liquidaciones
        )
          ? data.liquidaciones
          : [];

      setLiquidaciones(
        registros
      );

      setResumen({
        cantidad:
          Number(
            data?.resumen
              ?.cantidad ||
              registros.length
          ),

        totalLiquidadoUSD:
          Number(
            data?.resumen
              ?.totalLiquidadoUSD ||
              0
          ),

        totalPagadoUSD:
          Number(
            data?.resumen
              ?.totalPagadoUSD ||
              0
          ),

        saldoPendienteUSD:
          Number(
            data?.resumen
              ?.saldoPendienteUSD ||
              0
          ),
      });

      /*
        La primera carga es sin filtros.
        La usamos como catálogo de vendedores.
      */
      if (cargaInicial) {
        setVendedoresBase(
          registros
        );
      }
    } catch (err) {
      console.error(
        "Error informe liquidaciones:",
        err
      );

      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "No se pudo cargar el informe."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarReporte(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =======================================================
     LIMPIAR
     ======================================================= */

  const limpiarFiltros = () => {
    setDesde("");
    setHasta("");
    setSede("");
    setVendedor("");
    setTipo("");
    setEstado("");
  };

  /* =======================================================
     CSV
     ======================================================= */

  const generarCSV = () => {
    if (
      liquidaciones.length === 0
    ) {
      return;
    }

    const encabezados = [
      "Vendedor",
      "Sede",
      "Tipo",
      "Desde",
      "Hasta",
      "Productos",
      "Comision productos USD",
      "Bono USD",
      "Total liquidado USD",
      "Total pagado USD",
      "Saldo pendiente USD",
      "Estado",
    ];

    const escapar = (value) => {
      const texto =
        String(value ?? "");

      return `"${texto.replace(
        /"/g,
        '""'
      )}"`;
    };

    const filas =
      liquidaciones.map(
        (item) => [
          nombreVendedor(item),
          item?.sede || "",
          item?.tipo || "",
          formatDate(
            item?.periodoDesde
          ),
          formatDate(
            item?.periodoHasta
          ),
          Number(
            item?.cantidadProductos ||
              0
          ),
          Number(
            item
              ?.comisionProductosUSD ||
              0
          ),
          montoBono(item),
          Number(
            item
              ?.totalLiquidacionUSD ||
              0
          ),
          Number(
            item
              ?.totalPagadoUSD ||
              0
          ),
          Number(
            item
              ?.saldoPendienteUSD ||
              0
          ),
          textoEstado(
            item?.estado
          ),
        ]
      );

    const contenido = [
      encabezados,
      ...filas,
    ]
      .map((fila) =>
        fila
          .map(escapar)
          .join(";")
      )
      .join("\n");

    const blob = new Blob(
      [
        "\uFEFF",
        contenido,
      ],
      {
        type:
          "text/csv;charset=utf-8;",
      }
    );

    const url =
      URL.createObjectURL(blob);

    const link =
      document.createElement("a");

    link.href = url;

    link.download =
      "liquidaciones-comisiones-riders.csv";

    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

    URL.revokeObjectURL(url);
  };

  /* =======================================================
     PDF
     ======================================================= */

  const generarPDF = async () => {
    try {
      const doc =
        new jsPDF(
          "landscape",
          "mm",
          "a4"
        );

      const logoImg =
        await cargarImagen(logo);

      doc.addImage(
        logoImg,
        "PNG",
        10,
        8,
        34,
        12
      );

      doc.setFont(
        "helvetica",
        "bold"
      );

      doc.setFontSize(16);

      doc.text(
        "Informe de Liquidaciones de Comisiones",
        50,
        14
      );

      doc.setFont(
        "helvetica",
        "normal"
      );

      doc.setFontSize(10);

      doc.text(
        "Riders Miami",
        50,
        20
      );

      doc.text(
        `Desde: ${
          desde || "Todas"
        }   Hasta: ${
          hasta || "Todas"
        }`,
        287,
        14,
        {
          align: "right",
        }
      );

      const vendedorNombre =
        vendedoresDisponibles.find(
          (item) =>
            item.id === vendedor
        )?.nombre || "Todos";

      doc.text(
        `Sede: ${
          sede || "Todas"
        } | Vendedor: ${vendedorNombre} | Tipo: ${
          tipo || "Todos"
        } | Estado: ${
          estado
            ? textoEstado(estado)
            : "Todos"
        }`,
        287,
        20,
        {
          align: "right",
        }
      );

      doc.setDrawColor(180);

      doc.line(
        10,
        24,
        287,
        24
      );

      /* RESUMEN */

      const resumenY = 28;
      const boxW = 64;
      const boxH = 14;
      const gap = 4;

      const itemsResumen = [
        {
          label:
            "Liquidaciones",
          value:
            String(
              resumen.cantidad ||
                0
            ),
        },
        {
          label:
            "Total liquidado",
          value:
            formatUSDPdf(
              resumen
                .totalLiquidadoUSD
            ),
        },
        {
          label:
            "Total pagado",
          value:
            formatUSDPdf(
              resumen
                .totalPagadoUSD
            ),
        },
        {
          label:
            "Saldo pendiente",
          value:
            formatUSDPdf(
              resumen
                .saldoPendienteUSD
            ),
        },
      ];

      itemsResumen.forEach(
        (item, i) => {
          const x =
            10 +
            i *
              (boxW + gap);

          doc.setDrawColor(210);

          doc.roundedRect(
            x,
            resumenY,
            boxW,
            boxH,
            1.5,
            1.5
          );

          doc.setFont(
            "helvetica",
            "normal"
          );

          doc.setFontSize(8);

          doc.text(
            item.label,
            x + 2,
            resumenY + 4.5
          );

          doc.setFont(
            "helvetica",
            "bold"
          );

          doc.setFontSize(10);

          doc.text(
            String(item.value),
            x + 2,
            resumenY + 10
          );
        }
      );

      /* TABLA */

      const body =
        liquidaciones.map(
          (item) => [
            nombreVendedor(item),

            item?.sede || "",

            item?.tipo ===
            "MENSUAL"
              ? "Mensual"
              : "Semanal",

            formatDate(
              item?.periodoDesde
            ),

            formatDate(
              item?.periodoHasta
            ),

            Number(
              item
                ?.cantidadProductos ||
                0
            ),

            formatUSDPdf(
              item
                ?.comisionProductosUSD
            ),

            formatUSDPdf(
              montoBono(item)
            ),

            formatUSDPdf(
              item
                ?.totalLiquidacionUSD
            ),

            formatUSDPdf(
              item
                ?.totalPagadoUSD
            ),

            formatUSDPdf(
              item
                ?.saldoPendienteUSD
            ),

            textoEstado(
              item?.estado
            ),
          ]
        );

      autoTable(doc, {
        startY: 48,

        theme: "grid",

        margin: {
          left: 7,
          right: 7,
        },

        head: [
          [
            "Vendedor",
            "Sede",
            "Tipo",
            "Desde",
            "Hasta",
            "Prod.",
            "Comisión",
            "Bono",
            "Liquidado",
            "Pagado",
            "Saldo",
            "Estado",
          ],
        ],

        body,

        foot: [
          [
            "Totales",
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            formatUSDPdf(
              resumen
                .totalLiquidadoUSD
            ),
            formatUSDPdf(
              resumen
                .totalPagadoUSD
            ),
            formatUSDPdf(
              resumen
                .saldoPendienteUSD
            ),
            "",
          ],
        ],

        styles: {
          fontSize: 7,
          cellPadding: 1,
          valign: "middle",
        },

        headStyles: {
          fillColor: [
            33,
            37,
            41,
          ],
          textColor: 255,
          fontStyle: "bold",
          halign: "center",
        },

        footStyles: {
          fillColor: [
            245,
            245,
            245,
          ],
          textColor: 20,
          fontStyle: "bold",
        },

        columnStyles: {
          0: {
            cellWidth: 35,
          },

          1: {
            cellWidth: 20,
          },

          2: {
            cellWidth: 18,
          },

          3: {
            cellWidth: 19,
          },

          4: {
            cellWidth: 19,
          },

          5: {
            cellWidth: 13,
            halign: "center",
          },

          6: {
            cellWidth: 24,
            halign: "right",
          },

          7: {
            cellWidth: 22,
            halign: "right",
          },

          8: {
            cellWidth: 26,
            halign: "right",
          },

          9: {
            cellWidth: 24,
            halign: "right",
          },

          10: {
            cellWidth: 24,
            halign: "right",
          },

          11: {
            cellWidth: 20,
          },
        },

        didDrawPage: () => {
          const pageCount =
            doc.internal
              .getNumberOfPages();

          doc.setFontSize(8);

          doc.setTextColor(100);

          doc.text(
            `Página ${pageCount}`,
            doc.internal.pageSize
              .getWidth() - 10,
            doc.internal.pageSize
              .getHeight() - 6,
            {
              align: "right",
            }
          );
        },
      });

      doc.save(
        "liquidaciones-comisiones-riders.pdf"
      );
    } catch (error) {
      console.error(
        "Error generando PDF:",
        error
      );

      alert(
        "No se pudo generar el PDF."
      );
    }
  };

  /* =======================================================
     VISTA
     ======================================================= */

  return (
    <div className="container-fluid mt-3 mb-4">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <h4 className="mb-0">
                Informe de Liquidaciones
                de Comisiones
              </h4>

              <small className="text-muted">
                Riders Miami
              </small>
            </div>

            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={
                  limpiarFiltros
                }
              >
                Limpiar
              </Button>

              <Button
                variant="dark"
                size="sm"
                onClick={() =>
                  cargarReporte(false)
                }
                disabled={loading}
              >
                {loading
                  ? "Buscando..."
                  : "Buscar"}
              </Button>

              <Button
                variant="outline-success"
                size="sm"
                onClick={generarCSV}
                disabled={
                  loading ||
                  liquidaciones.length ===
                    0
                }
              >
                CSV
              </Button>

              <Button
                variant="success"
                size="sm"
                onClick={generarPDF}
                disabled={
                  loading ||
                  liquidaciones.length ===
                    0
                }
              >
                PDF
              </Button>
            </div>
          </div>

          {/* FILTROS */}

          <Row className="g-2 mb-2 align-items-end">
            <Col xs={12} sm={6} lg>
              <Form.Label>
                Desde
              </Form.Label>

              <Form.Control
                type="date"
                value={desde}
                onChange={(e) =>
                  setDesde(
                    e.target.value
                  )
                }
                size="sm"
              />
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Hasta
              </Form.Label>

              <Form.Control
                type="date"
                value={hasta}
                onChange={(e) =>
                  setHasta(
                    e.target.value
                  )
                }
                size="sm"
              />
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Sede
              </Form.Label>

              <Form.Select
                value={sede}
                onChange={(e) =>
                  setSede(
                    e.target.value
                  )
                }
                size="sm"
              >
                <option value="">
                  Todas
                </option>

                <option value="TUCUMAN">
                  Tucumán
                </option>

                <option value="YERBA_BUENA">
                  Yerba Buena
                </option>

                <option value="MIAMI">
                  Miami
                </option>
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Vendedor
              </Form.Label>

              <Form.Select
                value={vendedor}
                onChange={(e) =>
                  setVendedor(
                    e.target.value
                  )
                }
                size="sm"
              >
                <option value="">
                  Todos
                </option>

                {vendedoresDisponibles.map(
                  (item) => (
                    <option
                      key={item.id}
                      value={item.id}
                    >
                      {item.nombre}
                    </option>
                  )
                )}
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Tipo
              </Form.Label>

              <Form.Select
                value={tipo}
                onChange={(e) =>
                  setTipo(
                    e.target.value
                  )
                }
                size="sm"
              >
                <option value="">
                  Todos
                </option>

                <option value="SEMANAL">
                  Semanal
                </option>

                <option value="MENSUAL">
                  Mensual
                </option>
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} lg>
              <Form.Label>
                Estado
              </Form.Label>

              <Form.Select
                value={estado}
                onChange={(e) =>
                  setEstado(
                    e.target.value
                  )
                }
                size="sm"
              >
                <option value="">
                  Todos
                </option>

                <option value="PENDIENTE_PAGO">
                  Pendiente
                </option>

                <option value="PARCIAL">
                  Pago parcial
                </option>

                <option value="PAGADA">
                  Pagada
                </option>

                <option value="ANULADA">
                  Anulada
                </option>
              </Form.Select>
            </Col>
          </Row>

          <div className="small text-muted mb-3">
            Las fechas filtran por el
            inicio del período de la
            liquidación.
          </div>

          {error && (
            <div className="alert alert-danger py-2">
              {error}
            </div>
          )}

          {/* TOTALES */}

          <Row className="g-2 mb-3">
            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Liquidaciones
                  </small>

                  <h5 className="mb-0">
                    {resumen.cantidad ||
                      0}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Total liquidado
                  </small>

                  <h5 className="mb-0">
                    {formatUSD(
                      resumen
                        .totalLiquidadoUSD
                    )}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Total pagado
                  </small>

                  <h5 className="mb-0">
                    {formatUSD(
                      resumen
                        .totalPagadoUSD
                    )}
                  </h5>
                </Card.Body>
              </Card>
            </Col>

            <Col xs={12} sm={6} md={3}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="py-2">
                  <small className="text-muted">
                    Saldo pendiente
                  </small>

                  <h5 className="mb-0">
                    {formatUSD(
                      resumen
                        .saldoPendienteUSD
                    )}
                  </h5>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* TABLA */}

          <div
            className="table-responsive"
            style={{
              maxHeight: "65vh",
            }}
          >
            <Table
              striped
              bordered
              hover
              size="sm"
              className="align-middle"
            >
              <thead
                className="table-dark"
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                }}
              >
                <tr>
                  <th>Vendedor</th>

                  <th>Sede</th>

                  <th>Tipo</th>

                  <th>Período</th>

                  <th className="text-center">
                    Productos
                  </th>

                  <th className="text-end">
                    Comisión
                  </th>

                  <th className="text-end">
                    Bono
                  </th>

                  <th className="text-end">
                    Liquidado
                  </th>

                  <th className="text-end">
                    Pagado
                  </th>

                  <th className="text-end">
                    Saldo
                  </th>

                  <th>
                    Estado
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="11"
                      className="text-center py-4"
                    >
                      <Spinner
                        animation="border"
                        size="sm"
                        className="me-2"
                      />

                      Cargando informe...
                    </td>
                  </tr>
                ) : liquidaciones.length ===
                  0 ? (
                  <tr>
                    <td
                      colSpan="11"
                      className="text-center py-4 text-muted"
                    >
                      No hay liquidaciones
                      para los filtros
                      seleccionados.
                    </td>
                  </tr>
                ) : (
                  liquidaciones.map(
                    (item) => (
                      <tr
                        key={item._id}
                      >
                        <td className="fw-semibold">
                          {nombreVendedor(
                            item
                          )}
                        </td>

                        <td>
                          {item.sede}
                        </td>

                        <td>
                          <Badge
                            bg={
                              item.tipo ===
                              "MENSUAL"
                                ? "info"
                                : "secondary"
                            }
                          >
                            {item.tipo ===
                            "MENSUAL"
                              ? "Mensual"
                              : "Semanal"}
                          </Badge>
                        </td>

                        <td>
                          {formatDate(
                            item.periodoDesde
                          )}
                          {" → "}
                          {formatDate(
                            item.periodoHasta
                          )}
                        </td>

                        <td className="text-center">
                          {Number(
                            item
                              .cantidadProductos ||
                              0
                          )}
                        </td>

                        <td className="text-end">
                          {formatUSD(
                            item
                              .comisionProductosUSD
                          )}
                        </td>

                        <td className="text-end">
                          {formatUSD(
                            montoBono(
                              item
                            )
                          )}
                        </td>

                        <td className="text-end fw-bold">
                          {formatUSD(
                            item
                              .totalLiquidacionUSD
                          )}
                        </td>

                        <td className="text-end text-success">
                          {formatUSD(
                            item
                              .totalPagadoUSD
                          )}
                        </td>

                        <td className="text-end fw-bold">
                          {formatUSD(
                            item
                              .saldoPendienteUSD
                          )}
                        </td>

                        <td>
                          <Badge
                            bg={colorEstado(
                              item.estado
                            )}
                          >
                            {textoEstado(
                              item.estado
                            )}
                          </Badge>
                        </td>
                      </tr>
                    )
                  )
                )}
              </tbody>

              {liquidaciones.length >
                0 && (
                <tfoot>
                  <tr>
                    <th colSpan="7">
                      Totales
                    </th>

                    <th className="text-end">
                      {formatUSD(
                        resumen
                          .totalLiquidadoUSD
                      )}
                    </th>

                    <th className="text-end">
                      {formatUSD(
                        resumen
                          .totalPagadoUSD
                      )}
                    </th>

                    <th className="text-end">
                      {formatUSD(
                        resumen
                          .saldoPendienteUSD
                      )}
                    </th>

                    <th />
                  </tr>
                </tfoot>
              )}
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
}